import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSkinSession } from './SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import {
  getPageSetupDataKey,
  storePageSetupData,
} from '../components/PageBackgroundSync';
import { getPlanFlags } from '../config/skinAnalysisFeatureFlags';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { parseSegmentationPayload } from '../utils/segmentation';

const VALID_PLANS = ['standard', 'growth', 'pro'];

function normalizePlan(value) {
  if (value == null || value === '') return null;
  const p = String(value).toLowerCase().trim();
  return VALID_PLANS.includes(p) ? p : null;
}

const SkinAnalysisPlanContext = createContext(null);

function normalizeCaptureMode(value) {
  if (value == null || value === '') return null;
  const v = String(value).toLowerCase().trim();
  return v === 'side' || v === 'basic' ? v : null;
}

export function SkinAnalysisPlanProvider({ children }) {
  const { token } = useSkinSession();
  const [plan, setPlan] = useState(null);
  const [captureMode, setCaptureMode] = useState(null);
  const [aiMode, setAiMode] = useState(null);
  const [summaryAiMode, setSummaryAiMode] = useState(null);
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!token || !token.trim()) {
      setPlan(null);
      setCaptureMode(null);
      setAiMode(null);
      setSummaryAiMode(null);
      setAiGeneratedSummary(null);
      setIsReady(true);
      return;
    }

    const dataKey = getPageSetupDataKey(token);
    let cancelled = false;

    const applyPlan = (result) => {
      if (cancelled || !result) return;
      const externalFields =
        result?.external_fields ||
        result?.data?.external_fields ||
        (Array.isArray(result) && result[0]?.external_fields);
      const rawPlan = externalFields?.plan;
      setPlan(normalizePlan(rawPlan));
    };

    const applyCaptureMode = (result) => {
      if (cancelled) return;
      const rawCaptureMode =
        result?.capture_mode ??
        result?.data?.capture_mode ??
        (Array.isArray(result) && result[0]?.capture_mode);
      setCaptureMode(normalizeCaptureMode(rawCaptureMode));
      setIsReady(true);
    };

    const applyAiMode = (result) => {
      if (cancelled) return;
      const rawAiMode =
        result?.ai_mode ??
        result?.data?.ai_mode ??
        (Array.isArray(result) && result[0]?.ai_mode);
      const mode = rawAiMode && ['advanced', 'basic'].includes(String(rawAiMode).toLowerCase().trim())
        ? String(rawAiMode).toLowerCase().trim()
        : null;
      setSummaryAiMode(mode);
    };

    const applySegmentation = (result) => {
      if (cancelled || !result) return;
      const parsed = parseSegmentationPayload(result);
      setAiMode(parsed.aiMode);
      setAiGeneratedSummary(parsed.aiGeneratedSummary);
    };

    try {
      const stored = dataKey ? sessionStorage.getItem(dataKey) : null;
      if (stored) {
        const parsed = JSON.parse(stored);
        applyPlan(parsed);
      }
    } catch (_) {}

    const pageSetupUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup`;
    const captureUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/capture`;
    const summaryUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/summary`;
    const segmentationUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/segmentation`;

    fetchWithTimeout(pageSetupUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result) return;
        try {
          storePageSetupData(token, result);
        } catch (_) {}
        applyPlan(result);
      })
      .catch(() => {
        if (!cancelled) {
          setPlan(null);
        }
      });

    fetchWithTimeout(captureUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled) return;
        applyCaptureMode(result);
      })
      .catch(() => {
        if (!cancelled) {
          setCaptureMode(null);
          setIsReady(true);
        }
      });

    fetchWithTimeout(segmentationUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled) return;
        applySegmentation(result);
      })
      .catch(() => {
        if (!cancelled) {
          setAiMode(null);
          setAiGeneratedSummary(null);
        }
      });

    fetchWithTimeout(summaryUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled) return;
        applyAiMode(result);
      })
      .catch(() => {
        if (!cancelled) {
          setSummaryAiMode(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const flags = getPlanFlags(plan);
  // In salon flow, capture_mode is authoritative for side capture.
  // "side" => front+left+right, "basic" (or missing/invalid) => front only.
  const enableSideCapture = captureMode === 'side';
  // Segmentation ai_mode is authoritative; /summary is fallback when segmentation omits it.
  const resolvedAiMode = aiMode ?? summaryAiMode;
  const enableOllama = aiGeneratedSummary !== 'no';
  const value = {
    plan,
    captureMode,
    aiMode: resolvedAiMode,
    aiGeneratedSummary,
    isReady,
    ...flags,
    ENABLE_SIDE_CAPTURE: enableSideCapture,
    ENABLE_OLLAMA_DESCRIPTIONS: enableOllama,
    ENABLE_OLLAMA_SUMMARY: enableOllama,
    ENABLE_OLLAMA_ROUTINE: enableOllama,
  };

  return (
    <SkinAnalysisPlanContext.Provider value={value}>
      {children}
    </SkinAnalysisPlanContext.Provider>
  );
}

export function useSkinAnalysisPlan() {
  const ctx = useContext(SkinAnalysisPlanContext);
  if (!ctx) {
    throw new Error('useSkinAnalysisPlan must be used within SkinAnalysisPlanProvider');
  }
  return ctx;
}
